import {
  isCropTarget,
  isPrintDpi,
  svgIntrinsicSize,
  type CropDesignItem,
  type NormalizedCropRect,
  type PrintDpi,
} from './cropHelpers';
import { openCropModal } from './cropModal';
import { t } from './i18n';
import { resolveImageSourceUrls } from '../../../src/core/imagePipeline/resolveSource';
import { normalizeImageTransform } from '../../../src/core/imagePipeline/normalize';

type ImageToolContext = {
  item: CropDesignItem;
  update: (patch: Record<string, unknown>) => void;
};

type DesignerHost = {
  beginHistoryBatch?: () => void;
  endHistoryBatch?: (reason?: string) => void;
  updateItem?: (id: string, patch: Record<string, unknown>) => void;
};

type VeyopFacade = {
  hooks?: {
    on: (event: string, handler: (payload: unknown) => void) => void;
    whenReady?: (surface: 'designer', handler: (payload: unknown) => void) => () => void;
    applyFilters?: <T>(name: string, value: T, ...args: unknown[]) => T;
  };
  tools?: {
    registerImageTool: (tool: {
      id: string;
      label: string;
      priority?: number;
      render: (ctx: ImageToolContext) => HTMLElement;
    }) => () => void;
  };
  designer?: DesignerHost;
};

let registered = false;
let designerReady = false;
let runtime: VeyopFacade | null = null;
let closeActiveModal: (() => void) | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function designerHost(): DesignerHost | null {
  return (window as unknown as { Veyop?: VeyopFacade }).Veyop?.designer ?? runtime?.designer ?? null;
}

/** Current crop through the core normalizer, so designs saved with the legacy `imageCropper` reopen correctly. */
function readInitialCrop(item: CropDesignItem): NormalizedCropRect | null {
  const { crop } = normalizeImageTransform(item as never);
  if (!(crop.width > 0 && crop.height > 0)) return null;
  return { x: crop.x, y: crop.y, width: crop.width, height: crop.height };
}

/** Pixel size of the source the crop is normalized against. */
function sourceSize(item: CropDesignItem): { width: number; height: number } {
  const ex = item as Record<string, unknown>;
  const width = Number(ex.sourcePixelWidth) || 0;
  const height = Number(ex.sourcePixelHeight) || 0;
  if (width > 1 && height > 1) return { width, height };
  return (item.type === 'svg' ? svgIntrinsicSize(ex.svg) : null) ?? { width: 0, height: 0 };
}

/**
 * Preview candidates for the picture to crop, smallest usable first.
 *
 * Only renditions of the image the canvas shows are offered: an image replaced by older builds
 * only updated `src`, so `large` is trusted only while the shown source is one of the same
 * upload's renditions (`src` equal to `file`/`medium`/`large`); anything else (a
 * background-removed result, a replaced bitmap) is cropped as shown. The crop is normalized, so
 * the medium rendition crops exactly like the original; the modal skips any candidate whose
 * aspect differs from the source (a hard-cropped thumbnail) or that fails to load.
 */
function resolveSources(item: CropDesignItem): string[] {
  const ex = item as Record<string, unknown>;
  const text = (value: unknown) => String(value ?? '').trim();
  const shown = resolveImageSourceUrls(item as never)[0] ?? '';
  const large = text(ex.large);
  const medium = text(ex.medium) || text(ex.medium_url);
  const renditions = [text(ex.file), medium, large].filter(Boolean);
  const sameImage = Boolean(large) && renditions.includes(shown);
  const list = sameImage
    ? [medium, shown, large]
    : [shown, text(ex.src), large, text(ex.file)];
  return [...new Set(list.filter(Boolean))];
}

/** Print DPI from the `image.print.dpi` filter (served by Preflight); null when nobody answers. */
function printDpi(candidate: CropDesignItem): PrintDpi | null {
  const value = runtime?.hooks?.applyFilters?.<unknown>('image.print.dpi', null, candidate);
  return isPrintDpi(value) ? value : null;
}

function openCropEditor(item: CropDesignItem, ctx?: ImageToolContext): void {
  // Before hydration the item lacks its source size and the print context is not loaded.
  if (!designerReady || !isCropTarget(item)) return;
  closeActiveModal?.();

  const sources = resolveSources(item);
  if (!sources.length) return;
  const size = sourceSize(item);

  closeActiveModal = openCropModal({
    sources,
    sourceW: size.width,
    sourceH: size.height,
    vector: item.type === 'svg',
    item,
    initialCrop: readInitialCrop(item),
    printDpi,
    onCommit: (patch) => {
      const host = designerHost();
      if (host?.updateItem) {
        host.beginHistoryBatch?.();
        host.updateItem(item.id, patch);
        host.endHistoryBatch?.('crop');
      } else if (ctx?.update) {
        ctx.update(patch);
      } else {
        Object.assign(item, patch);
      }
      closeActiveModal = null;
    },
    onCancel: () => {
      closeActiveModal = null;
    },
  });
}

function createCropButton(ctx: ImageToolContext): HTMLElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'vptw:px-2! vptw:py-1! vptw:text-xs! vptw:rounded-xl! vptw:hover:bg-(--vs-surface-2)! vptw:transition! vptw:cursor-pointer! vptw:text-(--vs-ink-2)! vptw:flex! vptw:items-center! vptw:gap-1! vptw:shrink-0! vptw:p-0! vptw:border-0!';
  button.style.border = '0';
  button.style.height = '28px';
  button.style.minWidth = '46px';
  button.style.justifyContent = 'center';
  button.textContent = t('Crop');
  button.title = t('Crop image');

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    openCropEditor(ctx.item, ctx);
  });

  return button;
}

function register(Veyop: VeyopFacade): void {
  if (registered) return;
  if (!Veyop.tools?.registerImageTool || !Veyop.hooks?.on) return;
  registered = true;
  runtime = Veyop;

  Veyop.tools.registerImageTool({
    id: 'image-cropper.crop-tool',
    label: t('Crop image'),
    priority: 5,
    render: createCropButton,
  });

  Veyop.hooks.on('dblclick.image.design', (payload) => {
    if (!isRecord(payload) || !isCropTarget(payload.item)) return;
    openCropEditor(payload.item as CropDesignItem);
  });

  // Defensive: clear any stale active flag left by older saved designs.
  Veyop.hooks.on('before.save.design', () => {
    closeActiveModal?.();
  });

  const markReady = () => { designerReady = true; };
  if (Veyop.hooks.whenReady) Veyop.hooks.whenReady('designer', markReady);
  else Veyop.hooks.on('veyop:designer:ready', markReady);
}

/**
 * Addon bundles are module scripts after the designer bundle, which installs `window.Veyop`
 * synchronously, so the runtime is normally already there. A host that loaded addons first
 * still gets one retry once the document has parsed (module scripts have all run by then).
 */
function boot(): void {
  const Veyop = (window as unknown as { Veyop?: VeyopFacade }).Veyop;
  if (Veyop) {
    register(Veyop);
    return;
  }
  document.addEventListener('DOMContentLoaded', () => {
    const late = (window as unknown as { Veyop?: VeyopFacade }).Veyop;
    if (late) register(late);
  }, { once: true });
}

boot();
